import type { InvoiceSummary } from "@/shared/ui/InvoiceInfo";

/**
 * Singolo collo di una spedizione SDA multicollo. Il backend
 * (``/threads/{id}/context``) popola ``parcels`` solo per ordini SDA con
 * piu' colli; ``tracking_url`` puo' essere stringa vuota quando il carrier
 * non ha un URL di tracking noto (build_tracking_url → None lato backend).
 */
export interface ParcelInfo {
  code: string;
  tracking_url: string;
}

/** Customer-360 dell'ordine collegato (stesso calcolo dell'endpoint 360):
 *  LTV nella valuta dell'ordine, conteggio ordini, match debole (email
 *  assente → chiave nome+città), ultimi altri ordini del cliente. */
export interface Customer360 {
  ltv: string;
  currency: string;
  orders_count: number;
  is_fallback_key: boolean;
  other_orders: Array<{
    reference: string;
    date: string | null;
    total: string;
    state: string;
  }>;
}

/** Reso/sostituzione registrato sull'ordine del thread. */
export interface ReturnSummary {
  /** ``id`` + ``has_label`` dal 2026-10-16: tasto «Scarica etichetta». */
  id?: number;
  has_label?: boolean;
  kind: string;
  status: string;
  tracking_numbers: string[];
  /** Assenti nei payload precedenti al 2026-09-17: opzionali. */
  tracking_url?: string | null;
  recipient?: {
    company?: string;
    name?: string;
    surname?: string;
    city?: string;
    state_iso?: string;
  };
  created_at: string | null;
}

/** Sostituzioni fornitore aggregate (``cost_total`` = costo NETTO totale).
 *  ``senza_importo``: quante non hanno ancora l'importo — senza, un costo
 *  non arrivato si leggeva «€ 0,00 netto». */
export interface ReplacementsSummary {
  count: number;
  cost_total: number;
  senza_importo?: number;
}

/** Un riordino al fornitore (``raw.supplier_replacements``), costo NETTO.
 *  ``cost`` null = importo non ancora arrivato, mai 0. */
export interface RiordinoFornitore {
  rif: string | null;
  supplier_ref: string | null;
  at: string | null;
  pronta_il: string | null;
  cost: number | null;
}

/** Chiamata CRM registrata (per reference ordine o email cliente). */
export interface CallSummary {
  reason: string;
  note: string | null;
  order_reference: string | null;
  created_at: string | null;
}

export interface ContextOrder {
  id: number;
  reference: string;
  poleepo_id: number;
  state: string;
  payment_state: string;
  /** Poleepo raw status (es. ``READY_TO_SHIP``, ``FULL_SHIPPED``). */
  status: string;
  /** Data dell'ordine sul canale. Assente nei mock vecchi: opzionale. */
  created_at_poleepo?: string | null;
  /** Entro quando deve partire (marketplace). */
  ship_deadline?: string | null;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  shipping_address: string;
  shipping_postcode: string;
  shipping_city: string;
  shipping_province: string;
  shipping_country: string;
  carrier: string;
  tracking_code: string;
  supplier_order_ref: string;
  supplier_kind: string;
  /** Item 13: cliente non ancora pagato, anche a ordine spedito. */
  customer_unpaid?: boolean;
  /** Item 20: ``to_send`` = da ordinare, ``sent`` = ordinato. */
  supplier_send_state?: "to_send" | "sent" | null;
  cosma_sent_at?: string;
  /** Mail «merce pronta» del fornitore e mail LDV vista in Sent. */
  pickup_ready_at?: string | null;
  pickup_mail_sent_at?: string | null;
  /** Ultimo evento reale del corriere (job orario tracking_events_pull). */
  last_tracking_event?: {
    label?: string;
    location?: string;
    at?: string;
    delivered?: boolean;
    ritorno_mittente?: boolean;
  } | null;
  total_amount: string;
  currency: string;
  items: Array<{
    id: number;
    sku: string;
    title: string;
    quantity: number;
    unit_price: string;
    image_url: string;
  }>;
}

/**
 * Risposta backend ``/api/mail/threads/{id}/context``: ordine collegato al
 * thread + metadati. Tipi opzionali perche' un thread di marketplace puo' non
 * avere external_order_id (rare) o l'ordine puo' non essere ancora sincronizzato.
 */
export interface ThreadContextResponse {
  external_order_id: string;
  thread_subject: string;
  customer_name: string;
  source_label: string;
  /** Nome della casella («Maisons du Monde»): vince su ``source_label``. */
  account_label?: string;
  /** Slug source da MarketplaceLogo (es. ``amazon``, ``mirakl``). */
  source?: string;
  order: ContextOrder | null;
  /** Colli SDA multicollo (vuoto/assente per ordini singolo collo o non SDA). */
  parcels?: ParcelInfo[];
  /** Link pubblico della LDV principale; "" se il corriere non ne ha uno. */
  tracking_url?: string;
  /** Blocchi storico cliente: il backend li popola SOLO quando ``order`` !=
   *  null, nello stesso payload → il FE non fa fetch extra. */
  customer360?: Customer360;
  returns?: ReturnSummary[];
  replacements?: ReplacementsSummary;
  supplier_replacements?: RiordinoFornitore[];
  /** Nota di credito proposta: assente/null = nessun rimborso; ``amount``
   *  null = rimborso rilevato, importo da confermare. */
  refund?: { amount: string | null } | null;
  calls?: CallSummary[];
  /** Item 17: documento fiscale emesso per l'ordine collegato. */
  invoice?: InvoiceSummary | null;
}

/** Pratiche ancora in viaggio: il loro stato si legge dal corriere da solo
 *  all'apertura della chat (Davide 2026-10-14, «seguirla al volo»). */
export const PRATICA_APERTA = new Set(["richiesta", "etichetta_pronta", "in_transito"]);
